import React, { useEffect, useRef } from 'react'
import Navbar from '../Navbar'
import { Link, NavLink } from "react-router-dom";
import {
  Accordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel,
} from 'react-accessible-accordion';
import Footer from '../Footer';
import { SEO } from '../SEO';

function Icon({ id, open }: { id: any; open: any }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      className={`${id === open ? "rotate-180" : ""} h-5 w-5 transition-transform`}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
    </svg>
  );
}

function TuitionPaymentPage() {
  // Animation refs
  const heroRef = useRef(null);
  const featuresRef = useRef(null);
  const benefitsRef = useRef(null);
  const whyPayRef = useRef(null);
  const faqRef = useRef(null);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry: any) => {
        if (entry.isIntersecting) {
          // Hero animation
          if (entry.target === heroRef.current) {
            const elements = entry.target.querySelectorAll('.hero-item')
            elements.forEach((element: any, index: any) => {
              setTimeout(() => {
                element.classList.add('animate-fade-in-up')
              }, index * 100)
            })
          }

          // Features section animation
          if (entry.target === featuresRef.current) {
            // Animate title first
            const title = entry.target.querySelector('.initial-hidden')
            if (title && !title.classList.contains('feature-card')) {
              title.classList.add('animate-fade-in-up')
            }

            // Then animate cards
            const features = entry.target.querySelectorAll('.feature-card')
            features.forEach((feature: any, index: any) => {
              setTimeout(() => {
                feature.classList.add('animate-scale-in')
              }, (index + 1) * 150)
            })
          }

          // Benefits section animation
          if (entry.target === benefitsRef.current) {
            const image = entry.target.querySelector('.benefits-image')
            const content = entry.target.querySelector('.benefits-content')
            const title = entry.target.querySelector('.benefits-title')

            setTimeout(() => {
              if (image) image.classList.add('animate-slide-in-left')
            }, 100)

            setTimeout(() => {
              if (content) content.classList.add('animate-slide-in-right')
            }, 200)

            setTimeout(() => {
              if (title) title.classList.add('animate-fade-in-up')
            }, 300)

            // Animate benefit items
            const benefitItems = entry.target.querySelectorAll('.benefit-item')
            benefitItems.forEach((item: any, index: any) => {
              setTimeout(() => {
                item.classList.add('animate-fade-in-up')
              }, 400 + (index * 150))
            })
          }

          // Why pay with AfriPay animation
          if (entry.target === whyPayRef.current) {
            // Animate title first
            const title = entry.target.querySelector('h1.initial-hidden')
            if (title) {
              setTimeout(() => {
                title.classList.add('animate-fade-in-up')
              }, 100)
            }

            // Animate the 3 why items
            const items = entry.target.querySelectorAll('.why-item')
            items.forEach((item: any, index: any) => {
              setTimeout(() => {
                item.classList.add('animate-scale-in')
              }, (index + 1) * 150)
            })
          }

          // FAQ animation
          if (entry.target === faqRef.current) {
            // Animate title
            const title = entry.target.querySelector('h1.initial-hidden')
            if (title) {
              title.classList.add('animate-fade-in-up')
            }

            // Animate FAQ items
            const items = entry.target.querySelectorAll('.faq-item')
            items.forEach((item: any, index: any) => {
              setTimeout(() => {
                item.classList.add('animate-fade-in-up')
              }, (index + 1) * 100)
            })
          }
        }
      })
    }, observerOptions)

    const refs = [heroRef, featuresRef, benefitsRef, whyPayRef, faqRef]
    refs.forEach(ref => {
      if (ref.current) observer.observe(ref.current)
    })

    return () => observer.disconnect()
  }, [])

  return (
    <>
      <style>{`
                @keyframes fadeInUp {
                    from {
                        opacity: 0;
                        transform: translateY(30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                @keyframes slideInLeft {
                    from {
                        opacity: 0;
                        transform: translateX(-30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateX(0);
                    }
                }

                @keyframes slideInRight {
                    from {
                        opacity: 0;
                        transform: translateX(30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateX(0);
                    }
                }

                @keyframes scaleIn {
                    from {
                        opacity: 0;
                        transform: scale(0.9);
                    }
                    to {
                        opacity: 1;
                        transform: scale(1);
                    }
                }

                .animate-fade-in-up {
                    animation: fadeInUp 0.8s ease-out forwards;
                }

                .animate-slide-in-left {
                    animation: slideInLeft 0.8s ease-out forwards;
                }

                .animate-slide-in-right {
                    animation: slideInRight 0.8s ease-out forwards;
                }

                .animate-scale-in {
                    animation: scaleIn 0.8s ease-out forwards;
                }

                .initial-hidden {
                    opacity: 0;
                }

                .hover-lift {
                    transition: transform 0.3s ease, box-shadow 0.3s ease;
                }

                .hover-lift:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
                }

                .card-hover {
                    transition: all 0.3s ease;
                }

                .card-hover:hover {
                    transform: translateY(-3px);
                }
            `}</style>

      <SEO
        title="Tuition Payment | Afripay"
        description="Make Tuition and school fees payment on Afripay."
        canonical="https://goafripay.com/tuition-payment"
      />

      <section className='bg-gradient-to-b from-[#FAFBFC] to-[#F8FFFE]'>
        <Navbar />
        {/* Hero section */}
        <div className='pt-[120px]' ref={heroRef}>
          <div className="grid px-8 lg:px-16 pb-8 lg:gap-8 xl:gap-0 lg:pt-16 lg:grid-cols-12">
            <div className="mr-auto place-self-center lg:col-span-7">
              <div className="hero-item initial-hidden">
                <h1 className='text-sm sm:text-base text-[#00B07B] font-display font-semibold mb-2 uppercase tracking-wide'>AFRIPAY PAYMENT</h1>
              </div>
              <h1 className="hero-item initial-hidden max-w-2xl mb-6 text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-[#0a0a0a] font-display font-bold leading-tight tracking-[-0.02em]">
                Simplify Your Tuition Payments with AfriPay
              </h1>

              <p className="hero-item initial-hidden max-w-lg mb-8 text-base sm:text-lg text-[#525252] font-body font-medium leading-relaxed">
                Simplify tuition payments with AfriPay. Whether you're a student, parent, or institution, our secure platform streamlines fee management.
              </p>

              <div className="hero-item initial-hidden flex">
                <NavLink to="/afripay-app-waiting-list" className="">
                  <button
                    type="submit"
                    className="py-[14px] px-[30px] text-[16px] font-normal text-white bg-[#00B07B] rounded-[40px] border border-[#00B07B] hover:bg-[#009968] transition-all duration-300 hover:shadow-lg"
                  >
                    Get Started
                  </button>
                </NavLink>
              </div>
            </div>
            <div className="hero-item initial-hidden my-6 lg:mt-0 lg:col-span-5 lg:flex w-full md:mt-0 mt-[50px]">
              <img src="/images/tuition-heroo.avif" alt="hero" className="hover-lift" />
            </div>
          </div>

        </div>
      </section>

      <section className="bg-[#F6FDFE]" ref={featuresRef}>
        <div className="grid px-8 lg:px-16 pb-8 lg:gap-8 xl:gap-0 lg:pt-16">
          <div className='initial-hidden'>
            <h1 className='lg:text-[30px] text-[20px] font-bold font-display'>Incredible Features</h1>
          </div>
          <div className='grid lg:grid-cols-2 grid-cols-1 gap-5 pt-4'>
            <div className='feature-card initial-hidden bg-[#EEFEFC] px-[20px] py-[50px] rounded-[15px] card-hover'>
              <h3 className='lg:text-[20px] text-[14px] text-[#2C1762]'>Secure Transactions: Protect your payments with advanced encryption technology.</h3>
            </div>
            <div className='feature-card initial-hidden bg-[#FFE6F2] px-[20px] py-[50px] rounded-[15px] card-hover'>
              <h3 className='lg:text-[20px] text-[14px] text-[#2C1762]'>Automated Reminders: Set up reminders for upcoming payment deadlines to stay on track.</h3>
            </div>
            <div className='feature-card initial-hidden bg-[#FFFAEB] px-[20px] py-[50px] rounded-[15px] card-hover'>
              <h3 className='lg:text-[20px] text-[14px] text-[#2C1762]'>Real-time Confirmation: Receive instant confirmation of your tuition payment.</h3>
            </div>
            <div className='feature-card initial-hidden bg-[#F8F4FF] px-[20px] py-[50px] rounded-[15px] card-hover'>
              <h3 className='lg:text-[20px] text-[14px] text-[#2C1762]'>Track Payment History: Access your complete payment history for reference and record-keeping.</h3>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="bg-[#F6FDFE] md:pt-[50px] pt-[40px] pb-16 sm:px-16 px-8 flex justify-center items-start" ref={benefitsRef}>
        <div className='xl:max-w-[1280px] w-full'>
          <div className='flex md:flex-row flex-col'>

            <div className='benefits-image initial-hidden flex-1 flex justify-center items-center'>
              <img src="/images/tuition1.avif" alt="hero" className="hover-lift" />
            </div>

            <div className='benefits-content initial-hidden flex-1 flex justify-center items-start flex-col md:ml-[120px] ml-0'>
              <div className="flex space-x-5">

                <h1 className="benefits-title initial-hidden flex-1 font-display font-semibold md:text-[40px] text-[20px] md:mt-[20px] mt-[10px] text-[#1B212F]">
                  Benefits:
                </h1>
              </div>
              <div className='benefit-item initial-hidden flex  mt-[20px]'>
                <ul className=' list-disc list-outside space-y-2 text-[#979797] lg:text-[18px] text-[15px] pl-4'>
                  <li>Convenience: Say goodbye to long queues and paperwork with our online tuition payment platform.</li>
                  <li>Peace of Mind: Rest assured knowing that your tuition payments are handled securely and efficiently.</li>
                  <li>Accessibility: Access the tuition payment service anytime, anywhere, with just a few clicks.</li>
                  <li>Transparent Fees: Enjoy clear and transparent pricing with no hidden charges or surprises.</li>
                </ul>

              </div>


            </div>
          </div>
        </div>
      </section>

      <section className='bg-[#F6FDFE]' ref={whyPayRef}>
        <div className="grid px-8 lg:px-16 pb-8 lg:gap-8 xl:gap-0 lg:pt-8">
          <div className=''>
            <h1 className='lg:text-[30px] text-[20px] font-bold text-[#2C1762] font-display'>Why pay with AfriPay</h1>
          </div>
          <div className='grid lg:grid-cols-3 grid-cols-1 gap-5 pt-4'>
            <div className='why-item initial-hidden card-hover'>
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="48" height="48" rx="10" fill="#B48CFC" fill-opacity="0.2" />
                <path d="M23 27H18L25 13V21H30L23 35V27Z" fill="#B48CFC" />
              </svg>

              <h3 className='lg:text-[25px] text-[16px] font-bold text-[#2C1762] mt-1'>Convenience</h3>
              <p className='lg:w-[300px] text-[#5A617C] lg:text-[18px] text-[14px] mt-2'> Pay quickly and easily from your device.</p>
            </div>
            <div className='why-item initial-hidden card-hover'>
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="48" height="48" rx="10" fill="#FFCB2F" fill-opacity="0.1" />
                <path fill-rule="evenodd" clip-rule="evenodd" d="M15 20C15 16.229 15 14.343 16.172 13.172C17.343 12 19.229 12 23 12H25C28.771 12 30.657 12 31.828 13.172C33 14.343 33 16.229 33 20V24C33 27.771 33 29.657 31.828 30.828C30.657 32 28.771 32 25 32H23C19.229 32 17.343 32 16.172 30.828C15 29.657 15 27.771 15 24V20ZM18 22C18 20.586 18 19.879 18.44 19.44C18.878 19 19.585 19 21 19H27C28.414 19 29.121 19 29.56 19.44C30 19.879 30 20.586 30 22V26C30 27.414 30 28.121 29.56 28.56C29.121 29 28.414 29 27 29H21C19.586 29 18.879 29 18.44 28.56C18 28.122 18 27.415 18 26V22ZM19 15.25C18.8011 15.25 18.6103 15.329 18.4697 15.4697C18.329 15.6103 18.25 15.8011 18.25 16C18.25 16.1989 18.329 16.3897 18.4697 16.5303C18.6103 16.671 18.8011 16.75 19 16.75H24C24.1989 16.75 24.3897 16.671 24.5303 16.5303C24.671 16.3897 24.75 16.1989 24.75 16C24.75 15.8011 24.671 15.6103 24.5303 15.4697C24.3897 15.329 24.1989 15.25 24 15.25H19Z" fill="#FFCB2F" />
              </svg>


              <h3 className='lg:text-[25px] text-[16px] font-bold text-[#2C1762] mt-1'>Security</h3>
              <p className='lg:w-[300px] text-[#5A617C] lg:text-[18px] text-[14px] mt-2'> Your financial infomation is always protected.</p>
            </div>
            <div className='why-item initial-hidden card-hover'>
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="48" height="48" rx="10" fill="#00B07B" fill-opacity="0.14" />
                <path d="M18.002 34.5C17.3087 34.5 16.7183 34.2573 16.231 33.772C15.7437 33.286 15.5 32.696 15.5 32.002C15.5 31.3087 15.7433 30.7183 16.23 30.231C16.7153 29.7437 17.3053 29.5 18 29.5C18.1947 29.5 18.3987 29.5283 18.612 29.585C18.8247 29.641 19.0227 29.717 19.206 29.813L21.746 26.653C21.382 26.265 21.134 25.8357 21.002 25.365C20.87 24.895 20.8477 24.4183 20.935 23.935L17.198 22.702C16.9787 23.0927 16.6763 23.4067 16.291 23.644C15.905 23.8813 15.4747 24 15 24C14.3053 24 13.7153 23.7573 13.23 23.272C12.7433 22.786 12.5 22.196 12.5 21.502C12.5 20.8087 12.743 20.2183 13.229 19.731C13.7143 19.2437 14.3037 19 14.997 19C15.6903 19 16.2807 19.2433 16.768 19.73C17.2553 20.2153 17.499 20.8053 17.499 21.5C17.499 21.5847 17.4957 21.65 17.489 21.696C17.4823 21.7427 17.479 21.789 17.479 21.835L21.197 23.131C21.415 22.7103 21.7263 22.343 22.131 22.029C22.535 21.715 23.0103 21.5123 23.557 21.421V17.458C22.9197 17.338 22.4173 17.0413 22.05 16.568C21.6827 16.0947 21.499 15.5693 21.499 14.992C21.499 14.3053 21.742 13.7183 22.228 13.231C22.714 12.7437 23.304 12.5 23.998 12.5C24.6913 12.5 25.2817 12.7433 25.769 13.23C26.2563 13.7153 26.5 14.3053 26.5 15C26.5 15.572 26.3153 16.0947 25.946 16.568C25.5767 17.0413 25.0753 17.338 24.442 17.458V21.42C24.9887 21.5113 25.461 21.714 25.859 22.028C26.257 22.342 26.5713 22.7093 26.802 23.13L30.519 21.834C30.5063 21.7747 30.5 21.7253 30.5 21.686V21.5C30.5 20.8053 30.743 20.2153 31.229 19.73C31.7143 19.2433 32.304 19 32.998 19C33.6913 19 34.2817 19.243 34.769 19.729C35.2563 20.2143 35.5 20.804 35.5 21.498C35.5 22.1913 35.2567 22.7817 34.77 23.269C34.2853 23.7563 33.6953 24 33 24C32.5293 24 32.0993 23.8813 31.71 23.644C31.32 23.4067 31.0173 23.0927 30.802 22.702L27.065 23.935C27.1523 24.4183 27.13 24.8943 26.998 25.363C26.866 25.831 26.618 26.255 26.254 26.635L28.794 29.788C28.9773 29.7047 29.1753 29.636 29.388 29.582C29.6013 29.5273 29.805 29.5 29.999 29.5C30.6937 29.5 31.2843 29.743 31.771 30.229C32.257 30.7143 32.5 31.304 32.5 31.998C32.5 32.6913 32.2573 33.2817 31.772 33.769C31.286 34.2563 30.696 34.5 30.002 34.5C29.3087 34.5 28.7183 34.2567 28.231 33.77C27.7437 33.2847 27.5 32.6947 27.5 32C27.5 31.7027 27.5477 31.4163 27.643 31.141C27.739 30.8663 27.8977 30.6103 28.119 30.373L25.579 27.175C25.1263 27.4683 24.6023 27.615 24.007 27.615C23.411 27.615 22.8803 27.4683 22.415 27.175L19.881 30.373C20.0897 30.6103 20.245 30.8663 20.347 31.141C20.449 31.4163 20.5 31.7027 20.5 32C20.5 32.6947 20.2573 33.2847 19.772 33.77C19.286 34.2567 18.696 34.5 18.002 34.5Z" fill="#00B07B" />
              </svg>


              <h3 className='lg:text-[25px] text-[16px] font-bold text-[#2C1762] mt-1'>Transparency</h3>
              <p className='lg:w-[300px] text-[#5A617C] lg:text-[18px] text-[14px] mt-2'>Know exactly what you're paying, no hidden fees.</p>
            </div>

          </div>
        </div>
      </section>

      <section className='bg-[#F6FDFE] pt-10 pb-10 md:pt-20'>
        <div className=' flex-col px-8 lg:px-16'>

          <div className='bg-gradient-to-br from-[#00B07B] from-90% to-[#004A34] to-10% lg:rounded-[20px] rounded-[10px] lg:px-[50px] lg:pb-10 p-[20px] relative'>
            <div>
              <h3 className=' lg:block hidden lg:text-[32px]  text-white font-semibold font-display'>Simplify your Tuition Payments<br /> with Afripay</h3>
              <h3 className='block lg:hidden text-[20px] text-white font-semibold'>Simplify your Tuition Payments with Afripay</h3>
            </div>
            <div className='flex lg:flex-row lg:justify-between flex-col'>
              <div className='mt-5'>
                <p className='lg:w-[767px] text-[#DADADA] lg:text-[20px] text-[14px]'>Stay on top of your studies by simplifying your tuition payments with AfriPay. Pay now and spend more time focusing on what matters!</p>
              </div>
              <div className='lg:mr-[50px]'>

                <div className='mt-5'>
                  <NavLink to="/afripay-app-waiting-list" className="">
                    <button
                      type="submit"
                      className="py-[14px] px-[35px]  text-[16px] font-normal text-white rounded-[30px] border border-[#ff] "
                    >
                      Get Started
                    </button>
                  </NavLink>
                </div>
              </div>
            </div>
            <div className=' absolute right-0 -top-24'>
              <img src="/images/umb.png" alt="hero" />
            </div>
            <div className=' absolute -left-10 -bottom-24'>
              <img src="/images/cube.png" alt="hero" />
            </div>
          </div>

        </div>
      </section>


      <section className='py-[70px] px-8 sm:px-16' ref={faqRef}>
        <div className='flex justify-center items-center flex-col'>
          <h1 className='initial-hidden animate-fade-in-up lg:block hidden text-center font-display font-semibold text-[#2C1762] text-[36px] sm:text-[40px] md:text-[45px]'>Want to know more about <br />Tuition Payment?</h1>
          <h1 className='initial-hidden animate-fade-in-up block lg:hidden text-[18px] font-bold text-[#2C1762] text-center'>Want to know more about Tuition Payment?</h1>
          <div className='mx-auto lg:w-[798px] w-full mt-[30px]  sm:mt-[50px]'>
            <Accordion allowZeroExpanded allowMultipleExpanded>
              <AccordionItem className='faq-item initial-hidden card-hover bg-white border border-[#209D5A] px-3 rounded-[10px] mb-3'>
                <AccordionItemHeading>
                  <AccordionItemButton className=' py-[15px] mb-1 text-[15px] text-[#777777] flex justify-between'>
                    <span>How do I access AfriPay's Tuition Payment service?</span>
                    <span><svg width="18" height="19" viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M13.5 10.2485H9.75V13.9985C9.75 14.1974 9.67098 14.3882 9.53033 14.5288C9.38968 14.6695 9.19891 14.7485 9 14.7485C8.80109 14.7485 8.61032 14.6695 8.46967 14.5288C8.32902 14.3882 8.25 14.1974 8.25 13.9985V10.2485H4.5C4.30109 10.2485 4.11032 10.1695 3.96967 10.0288C3.82902 9.88818 3.75 9.69742 3.75 9.49851C3.75 9.29959 3.82902 9.10883 3.96967 8.96817C4.11032 8.82752 4.30109 8.74851 4.5 8.74851H8.25V4.9985C8.25 4.79959 8.32902 4.60883 8.46967 4.46817C8.61032 4.32752 8.80109 4.2485 9 4.2485C9.19891 4.2485 9.38968 4.32752 9.53033 4.46817C9.67098 4.60883 9.75 4.79959 9.75 4.9985V8.74851H13.5C13.6989 8.74851 13.8897 8.82752 14.0303 8.96817C14.171 9.10883 14.25 9.29959 14.25 9.49851C14.25 9.69742 14.171 9.88818 14.0303 10.0288C13.8897 10.1695 13.6989 10.2485 13.5 10.2485Z" fill="black" />
                    </svg>
                    </span>
                  </AccordionItemButton>
                </AccordionItemHeading>
                <AccordionItemPanel className='pb-6 text-[13px] text-[#777777]'>
                  <p>
                    You can access AfriPay's Tuition Payment service by securely logging in to your account from any device and navigating to the tuition payment section within AfriPay.
                  </p>
                </AccordionItemPanel>
              </AccordionItem>
              <AccordionItem className=' faq-item initial-hidden card-hover bg-white border border-[#209D5A] px-3 rounded-[10px] mb-3'>
                <AccordionItemHeading>
                  <AccordionItemButton className=' py-[15px] mb-1 text-[15px] text-[#777777] flex justify-between'>
                    <span>What payment details do I need to provide for tuition payment on AfriPay?</span>
                    <span><svg width="18" height="19" viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M13.5 10.2485H9.75V13.9985C9.75 14.1974 9.67098 14.3882 9.53033 14.5288C9.38968 14.6695 9.19891 14.7485 9 14.7485C8.80109 14.7485 8.61032 14.6695 8.46967 14.5288C8.32902 14.3882 8.25 14.1974 8.25 13.9985V10.2485H4.5C4.30109 10.2485 4.11032 10.1695 3.96967 10.0288C3.82902 9.88818 3.75 9.69742 3.75 9.49851C3.75 9.29959 3.82902 9.10883 3.96967 8.96817C4.11032 8.82752 4.30109 8.74851 4.5 8.74851H8.25V4.9985C8.25 4.79959 8.32902 4.60883 8.46967 4.46817C8.61032 4.32752 8.80109 4.2485 9 4.2485C9.19891 4.2485 9.38968 4.32752 9.53033 4.46817C9.67098 4.60883 9.75 4.79959 9.75 4.9985V8.74851H13.5C13.6989 8.74851 13.8897 8.82752 14.0303 8.96817C14.171 9.10883 14.25 9.29959 14.25 9.49851C14.25 9.69742 14.171 9.88818 14.0303 10.0288C13.8897 10.1695 13.6989 10.2485 13.5 10.2485Z" fill="black" />
                    </svg>
                    </span>
                  </AccordionItemButton>
                </AccordionItemHeading>
                <AccordionItemPanel className='pb-6 text-[13px] text-[#777777]'>
                  <p>
                    To make a tuition payment, you'll need to input necessary information such as the student's details, institution, and the amount to be paid.
                  </p>
                </AccordionItemPanel>
              </AccordionItem>
              <AccordionItem className='faq-item initial-hidden card-hover bg-white border border-[#209D5A] px-3 rounded-[10px] mb-3'>
                <AccordionItemHeading>
                  <AccordionItemButton className=' py-[15px] mb-1 text-[15px] text-[#777777] flex justify-between'>
                    <span>What payment methods are accepted for tuition payments on AfriPay?</span>
                    <span><svg width="18" height="19" viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M13.5 10.2485H9.75V13.9985C9.75 14.1974 9.67098 14.3882 9.53033 14.5288C9.38968 14.6695 9.19891 14.7485 9 14.7485C8.80109 14.7485 8.61032 14.6695 8.46967 14.5288C8.32902 14.3882 8.25 14.1974 8.25 13.9985V10.2485H4.5C4.30109 10.2485 4.11032 10.1695 3.96967 10.0288C3.82902 9.88818 3.75 9.69742 3.75 9.49851C3.75 9.29959 3.82902 9.10883 3.96967 8.96817C4.11032 8.82752 4.30109 8.74851 4.5 8.74851H8.25V4.9985C8.25 4.79959 8.32902 4.60883 8.46967 4.46817C8.61032 4.32752 8.80109 4.2485 9 4.2485C9.19891 4.2485 9.38968 4.32752 9.53033 4.46817C9.67098 4.60883 9.75 4.79959 9.75 4.9985V8.74851H13.5C13.6989 8.74851 13.8897 8.82752 14.0303 8.96817C14.171 9.10883 14.25 9.29959 14.25 9.49851C14.25 9.69742 14.171 9.88818 14.0303 10.0288C13.8897 10.1695 13.6989 10.2485 13.5 10.2485Z" fill="black" />
                    </svg>
                    </span>
                  </AccordionItemButton>
                </AccordionItemHeading>
                <AccordionItemPanel className='pb-6 text-[13px] text-[#777777]'>
                  <p>
                    AfriPay accepts various secure payment options, including credit/debit cards, mobile money, and bank transfers, providing flexibility and convenience for users.
                  </p>
                </AccordionItemPanel>
              </AccordionItem>
              <AccordionItem className='faq-item initial-hidden card-hover bg-white border border-[#209D5A] px-3 rounded-[10px] mb-3'>
                <AccordionItemHeading>
                  <AccordionItemButton className=' py-[15px] mb-1 text-[15px] text-[#777777] flex justify-between'>
                    <span>How can I ensure the security of my tuition payments on AfriPay?</span>
                    <span><svg width="18" height="19" viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M13.5 10.2485H9.75V13.9985C9.75 14.1974 9.67098 14.3882 9.53033 14.5288C9.38968 14.6695 9.19891 14.7485 9 14.7485C8.80109 14.7485 8.61032 14.6695 8.46967 14.5288C8.32902 14.3882 8.25 14.1974 8.25 13.9985V10.2485H4.5C4.30109 10.2485 4.11032 10.1695 3.96967 10.0288C3.82902 9.88818 3.75 9.69742 3.75 9.49851C3.75 9.29959 3.82902 9.10883 3.96967 8.96817C4.11032 8.82752 4.30109 8.74851 4.5 8.74851H8.25V4.9985C8.25 4.79959 8.32902 4.60883 8.46967 4.46817C8.61032 4.32752 8.80109 4.2485 9 4.2485C9.19891 4.2485 9.38968 4.32752 9.53033 4.46817C9.67098 4.60883 9.75 4.79959 9.75 4.9985V8.74851H13.5C13.6989 8.74851 13.8897 8.82752 14.0303 8.96817C14.171 9.10883 14.25 9.29959 14.25 9.49851C14.25 9.69742 14.171 9.88818 14.0303 10.0288C13.8897 10.1695 13.6989 10.2485 13.5 10.2485Z" fill="black" />
                    </svg>
                    </span>
                  </AccordionItemButton>
                </AccordionItemHeading>
                <AccordionItemPanel className='pb-6 text-[13px] text-[#777777]'>
                  <p>
                    AfriPay prioritizes the security of tuition payments by employing advanced encryption technology, ensuring that your transactions are protected and handled securely.
                  </p>
                </AccordionItemPanel>
              </AccordionItem>
              <AccordionItem className='faq-item initial-hidden card-hover bg-white border border-[#209D5A] px-3 rounded-[10px] mb-3'>
                <AccordionItemHeading>
                  <AccordionItemButton className=' py-[15px] mb-1 text-[15px] text-[#777777] flex justify-between'>
                    <span>Will I receive instant confirmation of my tuition payment on AfriPay?</span>
                    <span><svg width="18" height="19" viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M13.5 10.2485H9.75V13.9985C9.75 14.1974 9.67098 14.3882 9.53033 14.5288C9.38968 14.6695 9.19891 14.7485 9 14.7485C8.80109 14.7485 8.61032 14.6695 8.46967 14.5288C8.32902 14.3882 8.25 14.1974 8.25 13.9985V10.2485H4.5C4.30109 10.2485 4.11032 10.1695 3.96967 10.0288C3.82902 9.88818 3.75 9.69742 3.75 9.49851C3.75 9.29959 3.82902 9.10883 3.96967 8.96817C4.11032 8.82752 4.30109 8.74851 4.5 8.74851H8.25V4.9985C8.25 4.79959 8.32902 4.60883 8.46967 4.46817C8.61032 4.32752 8.80109 4.2485 9 4.2485C9.19891 4.2485 9.38968 4.32752 9.53033 4.46817C9.67098 4.60883 9.75 4.79959 9.75 4.9985V8.74851H13.5C13.6989 8.74851 13.8897 8.82752 14.0303 8.96817C14.171 9.10883 14.25 9.29959 14.25 9.49851C14.25 9.69742 14.171 9.88818 14.0303 10.0288C13.8897 10.1695 13.6989 10.2485 13.5 10.2485Z" fill="black" />
                    </svg>
                    </span>
                  </AccordionItemButton>
                </AccordionItemHeading>
                <AccordionItemPanel className='pb-6 text-[13px] text-[#777777]'>
                  <p>
                    Yes, upon confirming your tuition payment securely on AfriPay, you will receive instant confirmation, providing peace of mind and assurance that your payment has been processed.
                  </p>
                </AccordionItemPanel>
              </AccordionItem>
              <AccordionItem className='faq-item initial-hidden card-hover bg-white border border-[#209D5A] px-3 rounded-[10px] mb-3'>
                <AccordionItemHeading>
                  <AccordionItemButton className=' py-[15px] mb-1 text-[15px] text-[#777777] flex justify-between'>
                    <span>Can I set up reminders for upcoming tuition payment deadlines on AfriPay?</span>
                    <span><svg width="18" height="19" viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M13.5 10.2485H9.75V13.9985C9.75 14.1974 9.67098 14.3882 9.53033 14.5288C9.38968 14.6695 9.19891 14.7485 9 14.7485C8.80109 14.7485 8.61032 14.6695 8.46967 14.5288C8.32902 14.3882 8.25 14.1974 8.25 13.9985V10.2485H4.5C4.30109 10.2485 4.11032 10.1695 3.96967 10.0288C3.82902 9.88818 3.75 9.69742 3.75 9.49851C3.75 9.29959 3.82902 9.10883 3.96967 8.96817C4.11032 8.82752 4.30109 8.74851 4.5 8.74851H8.25V4.9985C8.25 4.79959 8.32902 4.60883 8.46967 4.46817C8.61032 4.32752 8.80109 4.2485 9 4.2485C9.19891 4.2485 9.38968 4.32752 9.53033 4.46817C9.67098 4.60883 9.75 4.79959 9.75 4.9985V8.74851H13.5C13.6989 8.74851 13.8897 8.82752 14.0303 8.96817C14.171 9.10883 14.25 9.29959 14.25 9.49851C14.25 9.69742 14.171 9.88818 14.0303 10.0288C13.8897 10.1695 13.6989 10.2485 13.5 10.2485Z" fill="black" />
                    </svg>
                    </span>
                  </AccordionItemButton>
                </AccordionItemHeading>
                <AccordionItemPanel className='pb-6 text-[13px] text-[#777777]'>
                  <p>
                    Yes, AfriPay's Tuition Payment service offers automated reminders, allowing you to set up reminders for upcoming payment deadlines to stay organized and on track.
                  </p>
                </AccordionItemPanel>
              </AccordionItem>
              <AccordionItem className='faq-item initial-hidden card-hover bg-white border border-[#209D5A] px-3 rounded-[10px] mb-3'>
                <AccordionItemHeading>
                  <AccordionItemButton className=' py-[15px] mb-1 text-[15px] text-[#777777] flex justify-between'>
                    <span>How can I track my tuition payment history on AfriPay?</span>
                    <span><svg width="18" height="19" viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M13.5 10.2485H9.75V13.9985C9.75 14.1974 9.67098 14.3882 9.53033 14.5288C9.38968 14.6695 9.19891 14.7485 9 14.7485C8.80109 14.7485 8.61032 14.6695 8.46967 14.5288C8.32902 14.3882 8.25 14.1974 8.25 13.9985V10.2485H4.5C4.30109 10.2485 4.11032 10.1695 3.96967 10.0288C3.82902 9.88818 3.75 9.69742 3.75 9.49851C3.75 9.29959 3.82902 9.10883 3.96967 8.96817C4.11032 8.82752 4.30109 8.74851 4.5 8.74851H8.25V4.9985C8.25 4.79959 8.32902 4.60883 8.46967 4.46817C8.61032 4.32752 8.80109 4.2485 9 4.2485C9.19891 4.2485 9.38968 4.32752 9.53033 4.46817C9.67098 4.60883 9.75 4.79959 9.75 4.9985V8.74851H13.5C13.6989 8.74851 13.8897 8.82752 14.0303 8.96817C14.171 9.10883 14.25 9.29959 14.25 9.49851C14.25 9.69742 14.171 9.88818 14.0303 10.0288C13.8897 10.1695 13.6989 10.2485 13.5 10.2485Z" fill="black" />
                    </svg>
                    </span>
                  </AccordionItemButton>
                </AccordionItemHeading>
                <AccordionItemPanel className='pb-6 text-[13px] text-[#777777]'>
                  <p>
                    AfriPay enables you to access your complete payment history for reference and record-keeping purposes, providing transparency and accountability.
                  </p>
                </AccordionItemPanel>
              </AccordionItem>
              <AccordionItem className='faq-item initial-hidden card-hover bg-white border border-[#209D5A] px-3 rounded-[10px] mb-3'>
                <AccordionItemHeading>
                  <AccordionItemButton className=' py-[15px] mb-1 text-[15px] text-[#777777] flex justify-between'>
                    <span>Are there any fees associated with using AfriPay's Tuition Payment service?</span>
                    <span><svg width="18" height="19" viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M13.5 10.2485H9.75V13.9985C9.75 14.1974 9.67098 14.3882 9.53033 14.5288C9.38968 14.6695 9.19891 14.7485 9 14.7485C8.80109 14.7485 8.61032 14.6695 8.46967 14.5288C8.32902 14.3882 8.25 14.1974 8.25 13.9985V10.2485H4.5C4.30109 10.2485 4.11032 10.1695 3.96967 10.0288C3.82902 9.88818 3.75 9.69742 3.75 9.49851C3.75 9.29959 3.82902 9.10883 3.96967 8.96817C4.11032 8.82752 4.30109 8.74851 4.5 8.74851H8.25V4.9985C8.25 4.79959 8.32902 4.60883 8.46967 4.46817C8.61032 4.32752 8.80109 4.2485 9 4.2485C9.19891 4.2485 9.38968 4.32752 9.53033 4.46817C9.67098 4.60883 9.75 4.79959 9.75 4.9985V8.74851H13.5C13.6989 8.74851 13.8897 8.82752 14.0303 8.96817C14.171 9.10883 14.25 9.29959 14.25 9.49851C14.25 9.69742 14.171 9.88818 14.0303 10.0288C13.8897 10.1695 13.6989 10.2485 13.5 10.2485Z" fill="black" />
                    </svg>
                    </span>
                  </AccordionItemButton>
                </AccordionItemHeading>
                <AccordionItemPanel className='pb-6 text-[13px] text-[#777777]'>
                  <p>
                    AfriPay offers clear and transparent pricing for its tuition payment service, ensuring that users enjoy transparent fees with no hidden charges or surprises
                  </p>
                </AccordionItemPanel>
              </AccordionItem>
              <AccordionItem className='faq-item initial-hidden card-hover bg-white border border-[#209D5A] px-3 rounded-[10px] mb-3'>
                <AccordionItemHeading>
                  <AccordionItemButton className=' py-[15px] mb-1 text-[15px] text-[#777777] flex justify-between'>
                    <span>Can I access AfriPay's Tuition Payment service anytime, anywhere?</span>
                    <span><svg width="18" height="19" viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M13.5 10.2485H9.75V13.9985C9.75 14.1974 9.67098 14.3882 9.53033 14.5288C9.38968 14.6695 9.19891 14.7485 9 14.7485C8.80109 14.7485 8.61032 14.6695 8.46967 14.5288C8.32902 14.3882 8.25 14.1974 8.25 13.9985V10.2485H4.5C4.30109 10.2485 4.11032 10.1695 3.96967 10.0288C3.82902 9.88818 3.75 9.69742 3.75 9.49851C3.75 9.29959 3.82902 9.10883 3.96967 8.96817C4.11032 8.82752 4.30109 8.74851 4.5 8.74851H8.25V4.9985C8.25 4.79959 8.32902 4.60883 8.46967 4.46817C8.61032 4.32752 8.80109 4.2485 9 4.2485C9.19891 4.2485 9.38968 4.32752 9.53033 4.46817C9.67098 4.60883 9.75 4.79959 9.75 4.9985V8.74851H13.5C13.6989 8.74851 13.8897 8.82752 14.0303 8.96817C14.171 9.10883 14.25 9.29959 14.25 9.49851C14.25 9.69742 14.171 9.88818 14.0303 10.0288C13.8897 10.1695 13.6989 10.2485 13.5 10.2485Z" fill="black" />
                    </svg>
                    </span>
                  </AccordionItemButton>
                </AccordionItemHeading>
                <AccordionItemPanel className='pb-6 text-[13px] text-[#777777]'>
                  <p>
                    Yes, AfriPay's tuition payment platform is accessible anytime, anywhere, allowing users to make payments with just a few clicks from the convenience of their preferred device.
                  </p>
                </AccordionItemPanel>
              </AccordionItem>
              <AccordionItem className='faq-item initial-hidden card-hover bg-white border border-[#209D5A] px-3 rounded-[10px] mb-3'>
                <AccordionItemHeading>
                  <AccordionItemButton className=' py-[15px] mb-1 text-[15px] text-[#777777] flex justify-between'>
                    <span>How can I receive notifications for my tuition payments on AfriPay?</span>
                    <span><svg width="18" height="19" viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M13.5 10.2485H9.75V13.9985C9.75 14.1974 9.67098 14.3882 9.53033 14.5288C9.38968 14.6695 9.19891 14.7485 9 14.7485C8.80109 14.7485 8.61032 14.6695 8.46967 14.5288C8.32902 14.3882 8.25 14.1974 8.25 13.9985V10.2485H4.5C4.30109 10.2485 4.11032 10.1695 3.96967 10.0288C3.82902 9.88818 3.75 9.69742 3.75 9.49851C3.75 9.29959 3.82902 9.10883 3.96967 8.96817C4.11032 8.82752 4.30109 8.74851 4.5 8.74851H8.25V4.9985C8.25 4.79959 8.32902 4.60883 8.46967 4.46817C8.61032 4.32752 8.80109 4.2485 9 4.2485C9.19891 4.2485 9.38968 4.32752 9.53033 4.46817C9.67098 4.60883 9.75 4.79959 9.75 4.9985V8.74851H13.5C13.6989 8.74851 13.8897 8.82752 14.0303 8.96817C14.171 9.10883 14.25 9.29959 14.25 9.49851C14.25 9.69742 14.171 9.88818 14.0303 10.0288C13.8897 10.1695 13.6989 10.2485 13.5 10.2485Z" fill="black" />
                    </svg>
                    </span>
                  </AccordionItemButton>
                </AccordionItemHeading>
                <AccordionItemPanel className='pb-6 text-[13px] text-[#777777]'>
                  <p>
                    AfriPay provides customizable notifications for payment receipts, pending payments, and more, allowing you to stay informed and updated on your tuition payment status according to your preferences.
                  </p>
                </AccordionItemPanel>
              </AccordionItem>
            </Accordion>
          </div>
        </div>

      </section>
      <Footer />
    </>
  )
}

export default TuitionPaymentPage